import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Box, Container, Divider, Grid, makeStyles, Typography } from "@material-ui/core";

import { PAGES } from "..";
import { login, exchangeSSOCode, authenticate } from "../../redux/auth/actions";
import { LogoText } from "../../components/logoText";
import userIcon from "../../assets/images/icons/user.svg";
import lockIcon from "../../assets/images/icons/lock.png";
import { AgreeTermCheck } from "../../components/agreeTermCheck";
import { TextInput, Button, TextButton, Image } from "../../components/base";
import buildingImg from "../../assets/images/onboarding/buildingWithTree.svg";
import { selectCluster } from "../../redux/selectCSC/actions";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
  },
  logo: {
    padding: theme.spacing(5),
    textAlign: "center",
  },
  title: {
    marginTop: theme.spacing(12),
    marginBottom: theme.spacing(6),
    fontWeight: 400,
  },
  groupBtn: {
    marginTop: theme.spacing(6),
  },
  welcomeTitle: {
    fontSize: 42,
    marginBottom: theme.spacing(2),
  },
}));

export const LoginPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, defaultEmail, cluster] = [
    params.get("username"),
    params.get("email"),
    params.get("cluster"),
  ];
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [agree, setAgree] = useState(true);
  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { redirect } = location.state || {};

  const [isSSOLoading, setIsSSOLoading] = useState(false);
  const [ssoError, setSSOError] = useState(false);
  console.log(isSSOLoading, ssoError);
  const codeExchanged = useRef(false);

  const code = params.get("code");

  useEffect(() => {
    if (!code || codeExchanged.current) return;

    const urlState = params.get("state");
    const savedState = sessionStorage.getItem("keycloak_state");

    if (!urlState || !savedState || urlState !== savedState) {
      console.error("State mismatch");
      setSSOError(true);
      sessionStorage.removeItem("keycloak_state");
      return;
    }

    sessionStorage.removeItem("keycloak_state");
    codeExchanged.current = true;
    setIsSSOLoading(true);

    dispatch(exchangeSSOCode({ code }))
      .then(data => {
        dispatch(authenticate(data));
        navigate("/buildings", { replace: true });
      })
      .catch((error) => {
        console.log(error)
        console.error("SSO login failed");
        setSSOError(true);
      })
      .finally(() => {
        setIsSSOLoading(false);
      });
  }, [code, params, navigate, dispatch]);

  const loginHandler = async () => {
    setErrors({});
    const errors = {};
    if (!email) {
      errors["email"] = "Please enter email";
    }
    if (!password) {
      errors["password"] = "Please enter password";
    }
    if (!agree) {
      errors["agree"] = true;
    }
    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);
    let data = { email, password };
    try {
      const response = await axios.get("https://geolocation-db.com/json/");
      if (response.status === 200) data["location"] = response.data;
    } catch (e) {
      console.log("Third party library error");
    }

    dispatch(login(data))
      .then((data) => {
        if (data.user?.clusters) dispatch(selectCluster(data.user.clusters[0]));
        navigate(redirect || PAGES.home);
      })
      .catch(() => {
        setErrors({
          email: "Email or password is not valid",
          password: "Email or password is not valid",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleSSOLogin = () => {
    const state = crypto.randomUUID();
    sessionStorage.setItem("keycloak_state", state);

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
      response_type: "code",
      redirect_uri: process.env.REACT_APP_KEYCLOAK_REDIRECT_URI,
      scope: "openid profile email",
      state,
    });

    window.location.href = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${
      process.env.REACT_APP_KEYCLOAK_REALM
    }/protocol/openid-connect/auth?${params.toString()}`;
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.logo}>
        <LogoText />
      </Box>
      <Divider />

      {username && (
        <Box display="flex" justifyContent="center" alignItems="center" my={8}>
          <Image src={buildingImg} width={120} />
          <Box ml={4}>
            <Typography className={classes.welcomeTitle} variant="h1">
              There You Are, {username}!
            </Typography>
            <Typography variant="h4">We&apos;ve been waiting for you...</Typography>
          </Box>
        </Box>
      )}

      <Container maxWidth="xs" style={{ maxWidth: 320 }}>
        <Grid container direction="column" alignItems="center">
          {!username && (
            <Typography className={classes.title} variant="h1" align="center">
              Hello, Please sign in
            </Typography>
          )}
          {username && (
            <Typography style={{ alignSelf: "start", marginBottom: 8 }}>
              Sign in to <b>{cluster} workspace</b>
            </Typography>
          )}
          <TextInput
            id="email-input"
            fullWidth
            placeHolder="Email"
            onChange={(v) => setEmail(v)}
            error={errors.email}
            value={email}
            onFocus={() => setErrors({ ...errors, email: null })}
            startAdornment={<img src={userIcon} alt="user-icon" />}
          />
          <TextInput
            id="password-input"
            type="password"
            fullWidth
            placeHolder="Password"
            onChange={(v) => setPassword(v)}
            value={password}
            error={errors.password}
            onFocus={() => setErrors({ ...errors, password: null })}
            startAdornment={<img src={lockIcon} alt="lock-icon" />}
          />
          <AgreeTermCheck agree={agree} setAgree={setAgree} error={errors.agree} />
          <Grid
            className={classes.groupBtn}
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              id="login-button"
              rootStyle={classes.loginBtn}
              text={username ? "LET’S GO!" : "LOGIN"}
              onClick={loginHandler}
              loading={loading}
            />
            <TextButton
              text="FORGOT PASSWORD"
              normalFontWeight
              onClick={() => navigate(PAGES.forgetPassword)}
            />
          </Grid>
        </Grid>
        <Divider style={{ margin: "16px 0" }} />
        <Grid container justifyContent="center" alignItems="center" direction="column" spacing={2}>
          <Box>
            {code && isSSOLoading ? (
              <Typography style={{ marginBottom: 8 }}>Signing you in</Typography>
            ) : null}
            {
              code && ssoError ? (
                <Typography style={{ marginBottom: 8 }}>SSO login failed</Typography>
              ) : null
            }
          </Box>
          <Box>
            <Button
              rootStyle={classes.loginBtn}
              onClick={handleSSOLogin}
              loading={isSSOLoading}
              text="SIGN IN WITH SSO"
            />
          </Box>
        </Grid>
      </Container>

    </Box>
  );
};
